// src/lib/tutor/on-screen-material.ts
//
// WHICH LESSON OR VIDEO IS ON SCREEN, AND WHICH PART OF IT THE STUDENT MEANS.
//
// QA: "a hint on worked example 3 in Boundaries" got "paste the example", and
// "how do I apply the technique from this Desmos video" got "which technique?".
// Sage knew the page's path and nothing else: the examples and the video's
// written walkthrough never reached it.
//
// Same pattern as active-question.ts: the lesson and video pages publish what's
// on screen; the app-shell TutorFab reads it into the page context it sends,
// and the server loads the content itself (material-context.ts). The client
// sends only ids — never the text — so nothing here can put words in Sage's
// mouth.
//
// The second half is the "Ask Sage about this example" bus: a button on a
// worked example or a video problem opens Sage pinned to exactly that item.

import { followThroughMessage } from './drill-follow-through'

/** A lesson, and optionally one of its worked examples (0-based). */
export type LessonRef = { slug: string; example?: number }
/** One of our videos, and optionally one of its worked problems (0-based). */
export type VideoRef = { collection: string; slug: string; problem?: number }

export type OnScreenMaterial = {
  lesson?: LessonRef
  video?: VideoRef
  /** What the panel header says it's answering about. Display only; not sent. */
  label?: string
}

type Listener = () => void

let material: OnScreenMaterial | null = null
const listeners = new Set<Listener>()

const same = (a: OnScreenMaterial | null, b: OnScreenMaterial | null) =>
  JSON.stringify(a) === JSON.stringify(b)

/**
 * Publish the lesson / video on screen (merged into what's there, so a lesson
 * page that also plays a video carries both), or clear a part with `null`.
 * Idempotent, so an effect may call it on every render.
 */
export function setOnScreenMaterial(patch: {
  lesson?: LessonRef | null
  video?: VideoRef | null
  label?: string | null
}): void {
  const next: OnScreenMaterial = { ...(material ?? {}) }
  for (const key of ['lesson', 'video', 'label'] as const) {
    if (!(key in patch)) continue
    const v = patch[key]
    if (v == null) delete next[key]
    else (next as Record<string, unknown>)[key] = v
  }
  const value = next.lesson || next.video ? next : null
  if (same(value, material)) return
  material = value
  for (const l of listeners) l()
}

export function getOnScreenMaterial(): OnScreenMaterial | null {
  return material
}

export function onOnScreenMaterialChange(cb: Listener): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/* ------------------------------------------------------------------ */
/*  "Ask Sage about this"                                              */
/* ------------------------------------------------------------------ */

export type AskSageRequest = {
  /** What Sage is answering about — overrides the page's own material. */
  material: OnScreenMaterial
  /** Put in the composer, NOT sent: the student edits it or just hits send. */
  draft: string
  /**
   * Anchor Sage to this question — "Continue in Sage" from the chat under a
   * practice question, whose saved transcript the server then carries over
   * (question-coaching.ts).
   */
  questionId?: string
  /**
   * SENT as soon as Sage opens, instead of a draft — for a button that says
   * on its face that it uses a Sage message ("What does this mean?" under a
   * finished Sage drill). Everything else seeds the composer with `draft`.
   */
  send?: string
  /** The finished Sage drill the message is about (ids only for the score). */
  drill?: SageDrillRef
}

/**
 * A finished Sage drill, as the results screen knows it. The server reads the
 * score from the student's own answers to `ids` (drill-follow-through-context.ts);
 * the rest is the drill link's naming.
 */
export type SageDrillRef = {
  ids: string[]
  topic?: string
  difficulty?: string
  label?: string
}

type AskListener = (req: AskSageRequest) => void
const askListeners = new Set<AskListener>()
const availabilityListeners = new Set<Listener>()

/**
 * The FAB registers here while it can actually open (signed in, not on a
 * hidden route). Ask buttons render only while someone is listening, so an
 * anonymous visitor on a public lesson never sees a button that does nothing.
 */
export function onAskSage(cb: AskListener): () => void {
  askListeners.add(cb)
  for (const l of availabilityListeners) l()
  return () => {
    askListeners.delete(cb)
    for (const l of availabilityListeners) l()
  }
}

export function canAskSage(): boolean {
  return askListeners.size > 0
}

export function onAskSageAvailability(cb: Listener): () => void {
  availabilityListeners.add(cb)
  return () => {
    availabilityListeners.delete(cb)
  }
}

/** Open Sage on one item. Returns false when nothing is listening. */
export function askSage(req: AskSageRequest): boolean {
  if (askListeners.size === 0) return false
  for (const cb of askListeners) cb(req)
  return true
}

/** "Example 3 · Boundaries lesson" and its draft. */
export function askAboutExample(slug: string, topic: string, index: number): AskSageRequest {
  return {
    material: {
      lesson: { slug, example: index },
      label: `Example ${index + 1} · ${topic} lesson`,
    },
    draft: `Can you give me a hint on Example ${index + 1}?`,
  }
}

/** "Problem 2 · <video title>", or the whole video when `index` is omitted. */
export function askAboutVideo(
  ref: { collection: string; slug: string; title: string },
  index?: number,
): AskSageRequest {
  const which = index == null ? null : `Problem ${index + 1}`
  return {
    material: {
      video: {
        collection: ref.collection,
        slug: ref.slug,
        ...(index == null ? {} : { problem: index }),
      },
      label: which ? `${which} · ${ref.title}` : `Video · ${ref.title}`,
    },
    draft: which
      ? `Can you walk me through ${which} from this video?`
      : 'How do I use the technique from this video on a real SAT question?',
  }
}

/**
 * "What does this mean?" under a finished Sage drill: opens Sage on that
 * drill's result and sends the question straight away (the button says it
 * uses a Sage message).
 */
export function askAboutDrill(drill: SageDrillRef): AskSageRequest {
  const topic = drill.topic?.trim() || null
  return {
    material: { label: topic ? `Your ${topic} drill` : 'Your drill' },
    draft: '',
    send: followThroughMessage(topic),
    drill,
  }
}

/** Test seam. */
export function resetOnScreenMaterialForTests(): void {
  material = null
  listeners.clear()
  askListeners.clear()
  availabilityListeners.clear()
}
